import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const InvoiceDetailsModal = ({ invoice, onClose, onDownload }) => {
  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      paid: { label: 'Payé', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
      pending: { label: 'En attente', className: 'bg-warning/10 text-warning', icon: 'Clock' },
      overdue: { label: 'En retard', className: 'bg-error/10 text-error', icon: 'AlertCircle' },
      cancelled: { label: 'Annulé', className: 'bg-muted text-muted-foreground', icon: 'XCircle' }
    };
    return configs?.[status] || configs?.pending;
  };

  const statusConfig = getStatusConfig(invoice?.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Détails de la facture</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Header */}
          <div className="flex items-start justify-between pb-6 border-b border-border">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-2">{invoice?.invoiceNumber}</h3>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusConfig?.className}`}>
                  <Icon name={statusConfig?.icon} size={16} />
                  {statusConfig?.label}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Montant total</p>
              <p className="text-3xl font-bold text-foreground">{formatAmount(invoice?.amount)}</p>
            </div>
          </div>

          {/* Patient & Case Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="User" size={18} />
                Informations patient
              </h4>
              <div className="flex items-center gap-3 mb-4">
                <Image
                  src={invoice?.patientAvatar}
                  alt={invoice?.patientAvatarAlt}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-foreground">{invoice?.patientName}</p>
                  <p className="text-sm text-muted-foreground">{invoice?.patientEmail}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Téléphone:</span>
                  <span className="font-medium text-foreground">{invoice?.patientPhone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID Patient:</span>
                  <span className="font-medium text-foreground">#{invoice?.patientId}</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Briefcase" size={18} />
                Informations de la Conception
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Numéro de la Conception:</span>
                  <span className="font-medium text-foreground">#{invoice?.caseId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type de traitement:</span>
                  <span className="font-medium text-foreground">{invoice?.treatmentType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dentiste:</span>
                  <span className="font-medium text-foreground">{invoice?.dentistName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Clinique:</span>
                  <span className="font-medium text-foreground">{invoice?.clinicName}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={18} />
              Détails de facturation
            </h4>
            <div className="space-y-3">
              {invoice?.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-start py-2 border-b border-border last:border-0">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{item?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item?.details}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-foreground">{formatAmount(item?.amount)}</p>
                    {item?.quantity > 1 && (
                      <p className="text-xs text-muted-foreground">
                        {item?.quantity} × {formatAmount(item?.unitPrice)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total:</span>
                  <span className="font-medium text-foreground">{formatAmount(invoice?.subtotal)}</span>
                </div>
                {invoice?.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">TVA (20%):</span>
                    <span className="font-medium text-foreground">{formatAmount(invoice?.tax)}</span>
                  </div>
                )}
                {invoice?.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-success">Remise:</span>
                    <span className="font-medium text-success">-{formatAmount(invoice?.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span className="text-foreground">Total:</span>
                  <span className="text-foreground">{formatAmount(invoice?.amount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Calendar" size={18} />
                Dates importantes
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date d'émission:</span>
                  <span className="font-medium text-foreground">{formatDate(invoice?.issueDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date d'échéance:</span>
                  <span className="font-medium text-foreground">{formatDate(invoice?.dueDate)}</span>
                </div>
                {invoice?.paidDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date de paiement:</span>
                    <span className="font-medium text-success">{formatDate(invoice?.paidDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {invoice?.paymentInfo && (
              <div className="bg-muted/30 rounded-lg p-4 border border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="CreditCard" size={18} />
                  Informations de paiement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Méthode:</span>
                    <span className="font-medium text-foreground">{invoice?.paymentInfo?.method}</span>
                  </div>
                  {invoice?.paymentInfo?.transactionId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transaction ID:</span>
                      <span className="font-medium text-foreground font-data">{invoice?.paymentInfo?.transactionId}</span>
                    </div>
                  )}
                  {invoice?.paymentInfo?.reference && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Référence:</span>
                      <span className="font-medium text-foreground">{invoice?.paymentInfo?.reference}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {invoice?.notes && (
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="MessageSquare" size={18} />
                Notes
              </h4>
              <p className="text-sm text-muted-foreground">{invoice?.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={() => onDownload(invoice)}
              iconName="Download"
              iconPosition="left"
            >
              Télécharger PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              iconName="Printer"
              iconPosition="left"
            >
              Imprimer
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Fermer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsModal;